import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowsClockwise, CalendarBlank, CheckCircle, Warning, CaretDown, CaretUp, AirplaneTilt, Clock, ChatText, PencilSimple, Trash, Plus, DownloadSimple, Crown, ArrowUp, ArrowDown, Check } from "@phosphor-icons/react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip as RTooltip, Legend, PieChart, Pie, Cell as PieCell } from "recharts";
import ManualRefreshButton from "../components/app/ManualRefreshButton";
import { toast } from "sonner";

const AC = { CI:"#1B52C4",MH:"#E8501A",CZ:"#10B981",CX:"#8B5CF6",SQ:"#F59E0B",QF:"#EF4444",MU:"#06B6D4",NZ:"#1E3A5F",JQ:"#FF6600",FJ:"#00A4BD",KE:"#003DA5",PR:"#0B4DA2" };
const FM = { BKK:"\ud83c\uddf9\ud83c\udded",SGN:"\ud83c\uddfb\ud83c\uddf3",HAN:"\ud83c\uddfb\ud83c\uddf3",TPE:"\ud83c\uddf9\ud83c\uddfc",BNE:"\ud83c\udde6\ud83c\uddfa",AKL:"\ud83c\uddf3\ud83c\uddff",MNL:"\ud83c\uddf5\ud83c\udded",NRT:"\ud83c\uddef\ud83c\uddf5",KIX:"\ud83c\uddef\ud83c\uddf5",ICN:"\ud83c\uddf0\ud83c\uddf7",CEB:"\ud83c\uddf5\ud83c\udded",LHR:"\ud83c\uddec\ud83c\udde7" };

function FareVal({v, meta, isCI}) {
  if (v === null && !meta?.has_no_flight && !meta?.cabin_not_offered) return <span className="text-gray-600 font-mono text-[10px]">&middot;&middot;&middot;</span>;
  if (meta?.has_no_flight) return <span className="text-gray-500 font-mono text-[10px] italic">&mdash;</span>;
  if (meta?.cabin_not_offered) return <span className="text-gray-500 font-mono text-[10px] italic">N/A</span>;
  return <span className="font-mono text-white text-xs">${v?.toLocaleString()}</span>;
}

function timeAgo(d) { if (!d) return "Never"; const s=(new Date()-new Date(d))/1000; if(s<3600) return `${Math.floor(s/60)}m ago`; if(s<86400) return `${Math.floor(s/3600)}h ago`; return `${Math.floor(s/86400)}d ago`; }

export default function RouteDetail() {
  const { routeId } = useParams(); const navigate = useNavigate(); const { api, user } = useAuth();
  const [fareData, setFareData] = useState(null); const [trendData, setTrendData] = useState(null); const [gapData, setGapData] = useState(null);
  const [weeklyData, setWeeklyData] = useState(null); const [weeklyLoading, setWeeklyLoading] = useState(false); const [weeklyRefreshing, setWeeklyRefreshing] = useState(false);
  const [capacityData, setCapacityData] = useState(null); const [notes, setNotes] = useState([]);
  const [disagreements, setDisagreements] = useState([]);
  const [promoData, setPromoData] = useState(null); const [promoLoading, setPromoLoading] = useState(false);
  const [allRoutes, setAllRoutes] = useState([]);
  const [featuredToday, setFeaturedToday] = useState(null);  // [{route_id, destination_code, fare_nzd}]
  const [promoRouteId, setPromoRouteId] = useState(null);  // null = viewing this route
  const fetchPromo = useCallback(async (targetId) => {
    const useId = targetId || promoRouteId || routeId;
    setPromoLoading(true);
    try {
      const { data } = await api.get(`/routes/${useId}/promo-tracker`);
      setPromoData(data);
    } catch { setPromoData({ error: true }); }
    finally { setPromoLoading(false); }
  }, [api, promoRouteId, routeId]);
  const fetchAllRoutes = useCallback(async () => {
    try {
      const { data } = await api.get("/routes");
      setAllRoutes(data || []);
      // Fetch promo status for ALL routes in parallel (all are fast <200ms)
      const results = await Promise.allSettled(
        (data || []).map(r => api.get(`/routes/${r.id}/promo-tracker`).then(x => ({ route: r, promo: x.data })))
      );
      const featured = results
        .filter(x => x.status === "fulfilled" && x.value.promo?.currently_featured)
        .map(x => ({
          route_id: x.value.route.id,
          destination_code: x.value.route.destination_code,
          origin_code: x.value.route.origin_code,
          fare_nzd: x.value.promo.current_fare_nzd,
        }));
      setFeaturedToday(featured);
    } catch { setFeaturedToday([]); }
  }, [api]);
  const tableScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft]   = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const handleTableScroll = () => {
    const el = tableScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  const scrollTable = (direction) => {
    const el = tableScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction === "right" ? 350 : -350, behavior: "smooth" });
  };
  const [monthsView, setMonthsView] = useState(6); const [refreshing, setRefreshing] = useState(false); const [loading, setLoading] = useState(true);
  const [cabinFilter, setCabinFilter] = useState("economy"); const [notesOpen, setNotesOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState("all"); const [posMonth, setPosMonth] = useState(0);

  const fetchFares = () => { setLoading(true); api.get(`/fares/${routeId}?months=${monthsView}`).then(r=>{setFareData(r.data);setLoading(false)}).catch(()=>setLoading(false)); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{fetchFares()}, [routeId, monthsView]);
  // Improvement 4: auto-poll every 60s so users see updated fares from any other user's refresh
  useEffect(() => {
    const t = setInterval(() => {
      api.get(`/fares/${routeId}?months=${monthsView}`).then(r => setFareData(r.data)).catch(()=>{});
    }, 60000);
    return () => clearInterval(t);
  }, [routeId, monthsView, api]);
  // Re-render every 30s so "X min ago" stays fresh
  // eslint-disable-next-line no-unused-vars
  const [_tick, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick(n => n + 1), 30000); return () => clearInterval(t); }, []);
  useEffect(()=>{
    api.get(`/fares/${routeId}/trends`).then(r=>setTrendData(r.data)).catch(()=>{});
    api.get(`/fares/${routeId}/gaps?months=${monthsView}`).then(r=>setGapData(r.data)).catch(()=>{});
    api.get(`/routes/${routeId}/notes`).then(r=>setNotes(r.data)).catch(()=>{});
    api.get(`/fares/disagreements`).then(r=>{
      const all = r.data?.disagreements || [];
      setDisagreements(all.filter(d => d.route_id === routeId));
    }).catch(()=>{});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId, monthsView]);

  // Update scroll button visibility after fares load
  useEffect(() => {
    const timer = setTimeout(() => handleTableScroll(), 300);
    return () => clearTimeout(timer);
  }, [fareData]);

  const handleRefresh = async () => { setRefreshing(true); try{await api.post(`/fares/refresh/${routeId}`);toast.success("Refreshed!");fetchFares()}catch(e){toast.error(e.response?.data?.detail||"Failed")} setRefreshing(false); };
  const handleVerify = async () => { try{const r=await api.post(`/routes/${routeId}/verify`);toast.success(`Verified by ${r.data.last_verified_by_name}`);fetchFares()}catch{toast.error("Failed")} };

  const route = fareData?.route; const isBNE = route?.destination_code === "BNE";
  const ciRow = fareData?.fare_matrix?.find(a=>a.is_our_airline); const ciPos = fareData?.ci_position;

  const fetchWeekly = () => { setWeeklyLoading(true); api.get(`/weekly-tracker/${routeId}`).then(r=>{setWeeklyData(r.data);setWeeklyLoading(false)}).catch(()=>setWeeklyLoading(false)); };
  const fetchCapacity = () => { api.get(`/routes/${routeId}/capacity`).then(r=>setCapacityData(r.data)).catch(()=>{}); };
  const handleWeeklyRefresh = async () => { setWeeklyRefreshing(true); try{await api.post("/weekly-tracker/refresh");toast.success("Updated!");fetchWeekly()}catch(e){toast.error("Failed")} setWeeklyRefreshing(false); };

  const getCellColor = (airline, mi, cc) => {
    if (airline.is_our_airline) { const v=airline.fares[mi]?.[cc]; if(!v) return ""; return airline.fares[mi]?.[`${cc}_is_cheapest`] ? "fare-cell-competitive" : ""; }
    const cv=airline.fares[mi]?.[cc], civ=ciRow?.fares[mi]?.[cc]; if(!cv||!civ) return "";
    const p=((civ-cv)/civ)*100; if(p>15) return "fare-cell-critical"; if(p>5) return "fare-cell-warning"; if(p>0) return "fare-cell-neutral"; return "";
  };

  const handleExportExcel = async () => {
    try {
      const resp = await api.get(`/fares/${routeId}/export/excel?months=${monthsView}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a'); a.href = url; a.download = `AKL-${route?.destination_code}_Fares.xlsx`; a.click();
    } catch { toast.error("Export failed"); }
  };

  const filteredMonths = useMemo(() => {
    if (!fareData?.months || selectedMonth === "all") return fareData?.months || [];
    return fareData.months.filter((_,i) => i === parseInt(selectedMonth));
  }, [fareData, selectedMonth]);

  // CI Position data for selected month
  const posData = useMemo(() => {
    if (!fareData?.fare_matrix || !fareData?.months?.length) return null;
    const mi = posMonth < fareData.months.length ? posMonth : 0;
    const m = fareData.months[mi]; if (!m) return null;
    const result = { month: m };
    for (const cc of ["economy","premium_economy","business"]) {
      const ci_fare = ciRow?.fares[mi]?.[cc];
      let cheapest = null;
      fareData.fare_matrix.filter(a=>!a.is_our_airline).forEach(a => {
        const f = a.fares[mi]?.[cc]; if (f && (!cheapest || f < cheapest.fare)) cheapest = { iata: a.airline_iata, name: a.airline_name, fare: f };
      });
      if (ci_fare && cheapest) {
        const gn = ci_fare - cheapest.fare; const gp = ci_fare > 0 ? (gn/ci_fare*100) : 0;
        result[cc] = { ci_fare, cheapest_competitor: cheapest, gap_nzd: gn, gap_percent: Math.round(gp*10)/10,
          status: gp <= 5 ? "competitive" : gp <= 15 ? "slightly_undercut" : "undercut", ci_is_cheapest: gn <= 0 };
      } else result[cc] = { ci_fare, cheapest_competitor: cheapest, status: ci_fare ? "competitive" : "no_data", ci_is_cheapest: !cheapest };
    }
    return result;
  }, [fareData, ciRow, posMonth]);

  const StatusCfg = { competitive: {border:"border-l-emerald-500",icon:CheckCircle,color:"text-success"}, slightly_undercut:{border:"border-l-amber-500",icon:Warning,color:"text-warning"}, undercut:{border:"border-l-red-500",icon:Warning,color:"text-danger"}, no_data:{border:"border-l-gray-500",icon:Clock,color:"text-text-secondary"} };

  // Source border indicator: blue = Google Flights (SerpAPI), orange = Airline Direct, green = Duffel, purple = Sources disagree
  const getSourceBorderStyle = (source, hasDisagreement = false) => {
    if (hasDisagreement) {
      return {
        borderLeft: "2px solid #8B5CF6",
        borderRadius: "0 8px 8px 0",
      };
    }
    if (!source) return {};
    if (source === "airline_direct") {
      return {
        borderLeft: "2px solid #E8501A",
        borderRadius: "0 8px 8px 0",
      };
    }
    if (source === "duffel") {
      return {
        borderLeft: "2px solid #10B981",
        borderRadius: "0 8px 8px 0",
      };
    }
    if (source === "kiwi") {
      return {
        borderLeft: "2px solid #06B6D4",
        borderRadius: "0 8px 8px 0",
      };
    }
    if (source === "brightdata") {
      return {
        borderLeft: "2px solid #0F6E56",
        borderRadius: "0 8px 8px 0",
      };
    }
    if (source === "zyte") {
      return {
        borderLeft: "2px solid #7C3AED",
        borderRadius: "0 8px 8px 0",
      };
    }
    return {
      borderLeft: "2px solid #1B52C4",
      borderRadius: "0 8px 8px 0",
    };
  };

  if (loading || !fareData) return <div className="space-y-4">{[1,2,3].map(i=><div key={i} className="skeleton h-16 rounded-xl"/>)}</div>;

  const trendChartData = (cc) => { if (!trendData?.trends) return []; return (fareData?.months||[]).map((m,i)=>{const p={month:m.label};Object.entries(trendData.trends).forEach(([iata,info])=>{p[iata]=info.data?.[cc]?.[i]?.fare});return p}); };

  return (
    <div data-testid="route-detail-page" className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button onClick={()=>navigate("/")} data-testid="back-to-dashboard" className="text-text-secondary hover:text-white"><ArrowLeft size={24}/></button>
          <div>
            <h1 className="font-syne font-bold text-2xl text-white tracking-tight flex items-center gap-2">{FM[route?.origin_code]} {route?.origin_code} <span className="text-text-secondary">{"\u2192"}</span> {route?.destination_code} {FM[route?.destination_code]}</h1>
            <div className="flex items-center gap-3 mt-0.5 flex-wrap">
              <p className="text-text-secondary text-sm">{route?.origin_city} to {route?.destination_city}</p>
              {fareData?.last_updated && (
                <span
                  data-testid="route-last-updated-pill"
                  className="inline-flex items-center gap-1.5 text-[10px] font-mono px-2 py-0.5 rounded-full bg-ci-blue/15 text-ci-blue border border-ci-blue/30"
                  title={`Latest fare in this matrix was fetched at ${new Date(fareData.last_updated).toLocaleString("en-NZ")}`}
                >
                  Updated {timeAgo(fareData.last_updated)}
                </span>
              )}
              {fareData?.last_updated && (
                <button
                  onClick={fetchFares}
                  data-testid="route-refresh-data-btn"
                  title="Pull the latest fares from the database now (does not call SerpAPI)"
                  className="inline-flex items-center gap-1 text-[10px] text-text-secondary hover:text-ci-blue px-2 py-0.5 rounded-full border border-[rgba(255,255,255,0.12)] hover:border-ci-blue/50 transition-colors"
                >
                  <ArrowsClockwise size={10} weight="bold" />
                  Refresh data
                </button>
              )}
              {route?.last_verified_at ? <span className="text-xs text-text-secondary flex items-center gap-1"><Check size={12} className="text-success"/> Verified {timeAgo(route.last_verified_at)}</span>
                : <span className="text-xs text-amber-400">Not yet verified</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-card-bg rounded-lg border border-[rgba(255,255,255,0.08)] p-0.5">
            {[6,9].map(m=><button key={m} data-testid={`month-toggle-${m}`} onClick={()=>setMonthsView(m)} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${monthsView===m?'bg-ci-blue text-white':'text-text-secondary hover:text-white'}`}>{m}M</button>)}
          </div>
          {(user?.role==="admin"||user?.role==="analyst") && <Button variant="outline" size="sm" onClick={handleVerify} data-testid="verify-btn" className="border-[rgba(255,255,255,0.15)] text-text-secondary hover:text-white h-8 text-xs"><Check size={14} className="mr-1"/>Verify</Button>}
          <Button variant="outline" size="sm" onClick={handleExportExcel} data-testid="export-excel-btn" className="border-[rgba(255,255,255,0.15)] text-text-secondary hover:text-white h-8 text-xs"><DownloadSimple size={14} className="mr-1"/>Excel</Button>
          <ManualRefreshButton prefillRouteIds={[routeId]} label="Refresh SerpAPI" onCompleted={fetchFares}/>
        </div>
      </div>

      {/* CI Position Card (E7) */}
      {posData && (
        <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} className="bg-card-bg border border-[rgba(255,255,255,0.08)] border-t-2 border-t-ci-orange rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-syne font-bold text-white text-sm">CI Position &mdash; {posData.month?.label}</h3>
            <div className="flex gap-1">{fareData.months.map((m,i)=><button key={i} onClick={()=>setPosMonth(i)} className={`px-2 py-0.5 text-[10px] rounded-full font-mono ${i===posMonth?'bg-ci-orange text-white':'text-text-secondary hover:text-white'}`}>{m.label?.split(' ')[0]}</button>)}</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {["economy","premium_economy","business"].map(cc => {
              const d = posData[cc] || {}; const cfg = StatusCfg[d.status] || StatusCfg.no_data; const Icon = cfg.icon;
              const ccLabel = cc==="economy"?"Economy (Y)":cc==="premium_economy"?"Premium Econ (PY)":"Business (C)";
              const posSource = ciPos?.[cc]?.source;
              return (
                <div key={cc} className={`bg-navy/40 rounded-lg p-3 border-l-4 ${cfg.border}`}>
                  <p className="text-text-secondary text-xs mb-1">{ccLabel}</p>
                  <div style={{ ...getSourceBorderStyle(posSource), paddingLeft: posSource ? "8px" : "0" }}>
                    <p className="font-mono text-white font-bold text-lg">{d.ci_fare ? `NZ$${d.ci_fare.toLocaleString()}` : '---'}</p>
                    {d.ci_is_cheapest && d.ci_fare ? <p className="text-success text-xs flex items-center gap-1 mt-1"><Crown size={12} weight="fill"/> CI Cheapest</p>
                     : d.cheapest_competitor ? <div className="mt-1">
                        <p className={`text-xs flex items-center gap-1 ${cfg.color}`}><Icon size={12} weight="fill"/> {d.cheapest_competitor.iata} NZ${d.cheapest_competitor.fare?.toLocaleString()}</p>
                        {d.gap_nzd != null && <p className={`font-mono text-xs ${cfg.color}`}>{d.gap_nzd>0?'-':''}${Math.abs(d.gap_nzd).toLocaleString()} ({d.gap_percent>0?'-':''}{Math.abs(d.gap_percent)}%)</p>}
                      </div> : <p className="text-text-secondary text-xs mt-1">No competitor data</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Disagreement summary panel — only renders when disagreements exist for this route */}
      {disagreements.length > 0 && (
        <motion.div
          data-testid="disagreement-panel"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border p-4"
          style={{ borderColor: "rgba(139, 92, 246, 0.3)", background: "rgba(139, 92, 246, 0.06)" }}
        >
          <div className="flex items-center gap-2 mb-3">
            <span style={{ color: "#8B5CF6", fontSize: "14px" }}>{"\u26a0"}</span>
            <span className="font-syne font-bold text-white text-sm">
              Source disagreements &mdash; {disagreements.length} {disagreements.length === 1 ? "cell" : "cells"}
            </span>
            <span style={{
              fontSize: "10px", color: "#8B5CF6",
              background: "rgba(139,92,246,0.15)",
              border: "1px solid rgba(139,92,246,0.25)",
              borderRadius: "4px", padding: "1px 6px",
              fontFamily: "monospace",
            }}>CI only</span>
          </div>
          <p style={{ fontSize: "11px", color: "#8EAADF", marginBottom: "10px" }}>
            Google Flights and CI&apos;s website show different prices for these cells.
            The fare table shows the lower price. Check CI&apos;s site directly to confirm.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {disagreements.map((d, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                background: "rgba(255,255,255,0.04)", borderRadius: "6px", padding: "6px 10px",
                fontSize: "11px", fontFamily: "monospace",
              }}>
                <span style={{ color: "#fff" }}>
                  {d.cabin_class.replace("_", " ")} &middot; {d.travel_month}/{d.travel_year}
                </span>
                <span style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                  <span style={{ color: "#8EAADF" }}>GF: <span style={{ color: "#fff" }}>${d.serpapi_fare?.toLocaleString()}</span></span>
                  <span style={{ color: "#8EAADF" }}>Direct: <span style={{ color: "#fff" }}>${d.direct_fare?.toLocaleString()}</span></span>
                  <span style={{ color: "#8B5CF6", fontWeight: "600" }}>{d.diff_percent}%</span>
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Route Notes (E3) */}
      {notes.length > 0 && (
        <div className="bg-card-bg border border-[rgba(255,255,255,0.08)] rounded-xl">
          <button onClick={()=>setNotesOpen(!notesOpen)} className="w-full flex items-center justify-between px-4 py-3 text-sm" data-testid="toggle-notes">
            <div className="flex items-center gap-2"><ChatText size={16} className="text-ci-orange"/><span className="text-white font-medium">Route Notes & Intelligence</span>
              <Badge className="bg-ci-orange/20 text-ci-orange border-ci-orange/30 text-xs">{notes.length}</Badge></div>
            {notesOpen ? <CaretUp size={16} className="text-text-secondary"/> : <CaretDown size={16} className="text-text-secondary"/>}
          </button>
          <AnimatePresence>{notesOpen && (
            <motion.div initial={{height:0,opacity:0}} animate={{height:"auto",opacity:1}} exit={{height:0,opacity:0}} className="overflow-hidden">
              <div className="px-4 pb-4 space-y-2">
                {notes.map((n,i)=>(
                  <div key={n.id||i} className="flex items-start gap-3 text-sm bg-navy/30 rounded-lg p-3">
                    <Badge variant="outline" className="text-[10px] shrink-0" style={{borderColor:AC[n.airline_iata]||'#666',color:AC[n.airline_iata]||'#666'}}>{n.airline_iata}</Badge>
                    <div><p className="text-white text-xs">{n.note_text}</p><p className="text-text-secondary text-[10px] mt-1">{n.note_type} &middot; {n.added_by_name} &middot; {timeAgo(n.created_at)}</p></div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}</AnimatePresence>
        </div>
      )}

      {/* Month Selector (E10) */}
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        <button onClick={()=>setSelectedMonth("all")} className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-all ${selectedMonth==="all"?'bg-ci-orange text-white':'bg-card-bg text-gray-400 border border-[rgba(255,255,255,0.08)] hover:text-white'}`}>All Months</button>
        {fareData.months.map((m,i)=><button key={i} onClick={()=>setSelectedMonth(String(i))} className={`px-3 py-1.5 text-xs font-mono rounded-full whitespace-nowrap transition-all ${selectedMonth===String(i)?'bg-ci-orange text-white':'bg-card-bg text-gray-400 border border-[rgba(255,255,255,0.08)] hover:text-white'}`}>{m.label}</button>)}
      </div>

      <Tabs defaultValue="table" className="space-y-4">
        <TabsList className="bg-card-bg border border-[rgba(255,255,255,0.08)]">
          <TabsTrigger value="table" data-testid="tab-fare-table" className="data-[state=active]:bg-ci-blue data-[state=active]:text-white text-xs">Fare Table</TabsTrigger>
          <TabsTrigger value="trends" data-testid="tab-trends" className="data-[state=active]:bg-ci-blue data-[state=active]:text-white text-xs">Trends</TabsTrigger>
          <TabsTrigger value="gaps" data-testid="tab-gaps" className="data-[state=active]:bg-ci-blue data-[state=active]:text-white text-xs">Gaps</TabsTrigger>
          <TabsTrigger value="capacity" data-testid="tab-capacity" onClick={()=>{if(!capacityData)fetchCapacity()}} className="data-[state=active]:bg-ci-blue data-[state=active]:text-white text-xs">Capacity</TabsTrigger>
          <TabsTrigger value="ci-promo" data-testid="tab-ci-promo" onClick={()=>{if(!promoData)fetchPromo(); if(!allRoutes.length)fetchAllRoutes();}} className="data-[state=active]:bg-ci-blue data-[state=active]:text-white text-xs">CI Promo</TabsTrigger>
          {isBNE && <TabsTrigger value="weekly" data-testid="tab-weekly" onClick={()=>{if(!weeklyData)fetchWeekly()}} className="data-[state=active]:bg-ci-blue data-[state=active]:text-white text-xs">Weekly OW</TabsTrigger>}
        </TabsList>

        {/* FARE TABLE */}
        <TabsContent value="table">
          <div style={{ position: "relative" }}>
            <div
              ref={tableScrollRef}
              onScroll={handleTableScroll}
              className="bg-card-bg border border-[rgba(255,255,255,0.08)] rounded-xl"
              style={{ overflowX: "auto" }}
            >
            <table className="w-full text-sm" data-testid="fare-table"><thead><tr className="border-b border-[rgba(255,255,255,0.08)]">
              <th className="sticky-col bg-card-bg text-left px-4 py-2 text-text-secondary font-medium text-xs uppercase min-w-[180px]">Airline</th>
              {filteredMonths.map(m=><th key={m.label} className="text-center px-2 py-2 min-w-[110px]"><div className="text-text-secondary font-medium text-xs">{m.label}</div>{m.date_range_label && <div className="text-gray-500 text-[10px] font-mono">{m.date_range_label}</div>}</th>)}
            </tr>
            <tr data-testid="fare-source-legend">
              <td colSpan={999} style={{ paddingBottom: "8px", paddingTop: "4px", paddingLeft: "16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "10px", color: "#8EAADF" }}>
                    <span style={{ display: "inline-block", width: "3px", height: "12px", background: "#1B52C4", borderRadius: "1px" }} />
                    Google Flights
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "10px", color: "#8EAADF" }}>
                    <span style={{ display: "inline-block", width: "3px", height: "12px", background: "#E8501A", borderRadius: "1px" }} />
                    Airline direct
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "10px", color: "#8EAADF" }}>
                    <span style={{ display: "inline-block", width: "3px", height: "12px", background: "#10B981", borderRadius: "1px" }} />
                    Duffel
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "10px", color: "#8EAADF" }}>
                    <span style={{ display: "inline-block", width: "3px", height: "12px", background: "#06B6D4", borderRadius: "1px" }} />
                    Kiwi
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "10px", color: "#8EAADF" }}>
                    <span style={{ display: "inline-block", width: "3px", height: "12px", background: "#0F6E56", borderRadius: "1px" }} />
                    Bright Data (airline direct)
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "10px", color: "#8EAADF" }}>
                    <span style={{ display: "inline-block", width: "3px", height: "12px", background: "#7C3AED", borderRadius: "1px" }} />
                    Zyte (AI browser)
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "10px", color: "#8EAADF" }}>
                    <span style={{ display: "inline-block", width: "3px", height: "12px", background: "#8B5CF6", borderRadius: "1px" }} />
                    Sources disagree
                  </span>
                </div>
              </td>
            </tr>
            </thead><tbody>
              {fareData.fare_matrix.map((airline,ai) => {
                const displayMonths = selectedMonth==="all" ? airline.fares : [airline.fares[parseInt(selectedMonth)]].filter(Boolean);
                const monthIndices = selectedMonth==="all" ? airline.fares.map((_,i)=>i) : [parseInt(selectedMonth)];
                return (
                <motion.tr key={airline.airline_iata} initial={{opacity:0}} animate={{opacity:1}} transition={{delay:ai*0.04}}
                  className={`border-b border-[rgba(255,255,255,0.06)] hover:bg-white/5 ${airline.is_our_airline?'ci-row':''}`}>
                  <td className="sticky-col bg-card-bg px-4 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-8 rounded-full" style={{backgroundColor:AC[airline.airline_iata]||'#666'}}/>
                      <div>
                        <div className="flex items-center gap-1.5"><span className="text-white font-medium text-sm">{airline.airline_iata}</span><span className="text-text-secondary text-xs hidden sm:inline">{airline.airline_name}</span>
                          {notes.some(n=>n.airline_iata===airline.airline_iata) && <TooltipProvider><Tooltip><TooltipTrigger><ChatText size={12} className="text-ci-orange"/></TooltipTrigger>
                            <TooltipContent className="bg-navy border border-[rgba(255,255,255,0.15)] text-white max-w-[300px]"><div className="text-xs space-y-1">{notes.filter(n=>n.airline_iata===airline.airline_iata).map((n,i)=><p key={i}>{n.note_text}</p>)}</div></TooltipContent></Tooltip></TooltipProvider>}
                        </div>
                        {(airline.weekly_frequency||airline.flight_hours) && <div className="flex items-center gap-2 mt-0.5 text-gray-500 text-[10px] font-mono">
                          {airline.weekly_frequency && <span className="flex items-center gap-0.5"><AirplaneTilt size={10}/>{airline.weekly_frequency.split('(')[0].trim()}</span>}
                          {airline.flight_hours && <span className="flex items-center gap-0.5"><Clock size={10}/>{airline.flight_hours}</span>}
                        </div>}
                      </div>
                    </div>
                  </td>
                  {displayMonths.map((fare, di) => {
                    const mi = monthIndices[di];
                    return (
                    <td key={di} className="px-2 py-1.5 text-center">
                      <TooltipProvider delayDuration={200}><div className="space-y-0.5">
                        {[{l:"C",cc:"business"},{l:"PY",cc:"premium_economy"},{l:"Y",cc:"economy"}].map(({l,cc}) => {
                          const v=fare[cc], meta=fare[`${cc}_meta`], color=getCellColor(airline,mi,cc);
                          const gapNzd=fare[`${cc}_gap_nzd`], gapPct=fare[`${cc}_gap_percent`];
                          const isCheapest = fare[`${cc}_is_cheapest`];
                          const disagreement = airline.is_our_airline ? fare[`${cc}_disagreement`] : null;
                          return (
                            <Tooltip key={l}><TooltipTrigger asChild>
                              <div
                                data-testid={`fare-cell-${airline.airline_iata}-${cc}-${mi}`}
                                className={`px-1.5 py-0.5 rounded text-[10px] ${color}`}
                                style={v != null ? getSourceBorderStyle(fare[`${cc}_source`], !!disagreement) : {}}
                                title={disagreement ? `Source disagreement: Google Flights $${disagreement.serpapi_fare?.toLocaleString()} vs Airline direct $${disagreement.direct_fare?.toLocaleString()} (${disagreement.diff_percent}% difference)` : undefined}
                              >
                                <span className="text-text-secondary mr-0.5">{l}</span>
                                <FareVal v={v} meta={meta} isCI={airline.is_our_airline}/>
                                {meta?.is_override && <span className="ml-0.5 text-warning">&#128204;</span>}
                                {meta?.sample_offset_days != null && meta.sample_offset_days !== 0 && (
                                  <span
                                    data-testid={`fare-cell-offset-${airline.airline_iata}-${cc}-${mi}`}
                                    title={`Found on ${meta.sample_offset_days > 0 ? "+" : ""}${meta.sample_offset_days} day fallback (${meta.outbound_date || "?"})`}
                                    className="ml-1 text-[8px] font-mono px-1 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30"
                                  >
                                    {meta.sample_offset_days > 0 ? "+" : ""}{meta.sample_offset_days}d
                                  </span>
                                )}
                                {/* E6: Gap in cells */}
                                {!airline.is_our_airline && v && gapNzd != null && (
                                  <div className={`text-[9px] font-mono ${gapNzd<0?'text-danger':'text-success'}`}>{gapNzd<0?'':'+'}${gapNzd.toLocaleString()} {gapNzd<0?'\u25bc':'\u25b2'}</div>
                                )}
                                {airline.is_our_airline && v && isCheapest && <div className="text-[9px] text-success font-medium">{'\ud83d\udc51'}</div>}
                                {disagreement && (
                                  <span style={{ fontSize: "9px", color: "#8B5CF6", fontFamily: "monospace", marginTop: "2px", display: "block", opacity: 0.85 }}>
                                    {"\u26a0"} {disagreement.diff_percent}% diff
                                  </span>
                                )}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="bg-navy border border-[rgba(255,255,255,0.15)] text-white max-w-xs"><div className="text-xs space-y-1">
                              {meta?.has_no_flight ? <p className="text-gray-400 italic">No flights on these dates</p>
                               : meta?.cabin_not_offered ? <p className="text-gray-400 italic">Cabin not offered</p>
                               : v ? <><p className="font-mono font-bold">NZ${v.toLocaleString()}</p>
                                  {meta?.stops!=null && <p>{meta.stops===0?'Non-stop':`${meta.stops} stop${meta.stops>1?'s':''}`}</p>}
                                  {meta?.fetched_at && <p>Fetched: {new Date(meta.fetched_at).toLocaleString()}</p>}</>
                               : <p className="text-gray-400 italic">Not yet fetched</p>}
                            </div></TooltipContent></Tooltip>
                          );
                        })}
                      </div></TooltipProvider>
                    </td>);
                  })}
                </motion.tr>);
              })}
            </tbody></table>
          </div>
          {canScrollLeft && (
            <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "80px",
              background: "linear-gradient(to right, rgba(15,32,68,0.6), transparent)",
              zIndex: 20, pointerEvents: "none" }} />
          )}
          {canScrollRight && (
            <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: "80px",
              background: "linear-gradient(to left, rgba(15,32,68,0.6), transparent)",
              zIndex: 20, pointerEvents: "none" }} />
          )}
          {canScrollLeft && (
            <button
              data-testid="fare-scroll-left"
              onClick={() => scrollTable("left")}
              aria-label="Scroll left"
              style={{
                position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)",
                zIndex: 30, width: "44px", height: "44px", borderRadius: "50%",
                background: "#1B52C4", border: "none", color: "#FFFFFF", fontSize: "22px",
                lineHeight: "1", display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
                transition: "background 0.15s, transform 0.15s", userSelect: "none", pointerEvents: "all",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#E8501A"; e.currentTarget.style.transform = "translateY(-50%) scale(1.12)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#1B52C4"; e.currentTarget.style.transform = "translateY(-50%) scale(1)"; }}
            >
              {"\u2039"}
            </button>
          )}
          {canScrollRight && (
            <button
              data-testid="fare-scroll-right"
              onClick={() => scrollTable("right")}
              aria-label="Scroll right"
              style={{
                position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)",
                zIndex: 30, width: "44px", height: "44px", borderRadius: "50%",
                background: "#1B52C4", border: "none", color: "#FFFFFF", fontSize: "22px",
                lineHeight: "1", display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
                transition: "background 0.15s, transform 0.15s", userSelect: "none", pointerEvents: "all",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#E8501A"; e.currentTarget.style.transform = "translateY(-50%) scale(1.12)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#1B52C4"; e.currentTarget.style.transform = "translateY(-50%) scale(1)"; }}
            >
              {"\u203A"}
            </button>
          )}
          </div>
          {canScrollRight && (
            <div style={{
              display: "flex", justifyContent: "center", alignItems: "center",
              gap: "6px", marginTop: "10px", fontSize: "11px", color: "#8EAADF", letterSpacing: "0.02em",
            }}>
              <span style={{ fontSize: "14px" }}>{"\u203A"}</span>
              <span>Scroll right to see all months</span>
              <span style={{ fontSize: "14px" }}>{"\u203A"}</span>
            </div>
          )}
        </TabsContent>

        {/* TRENDS */}
        <TabsContent value="trends">
          <div className="space-y-4">
            <div className="flex gap-2">{["economy","premium_economy","business"].map(cc=><button key={cc} onClick={()=>setCabinFilter(cc)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${cabinFilter===cc?'bg-ci-blue text-white':'bg-card-bg text-text-secondary border border-[rgba(255,255,255,0.08)]'}`}>{cc==='economy'?'Economy (Y)':cc==='premium_economy'?'Premium (PY)':'Business (C)'}</button>)}</div>
            <div className="bg-card-bg border border-[rgba(255,255,255,0.08)] rounded-xl p-6"><h3 className="font-syne font-bold text-white mb-4">{cabinFilter==='economy'?'Economy':cabinFilter==='premium_economy'?'Premium Economy':'Business'} Trends</h3>
              <div className="h-72"><ResponsiveContainer width="100%" height="100%"><LineChart data={trendChartData(cabinFilter)}>
                <XAxis dataKey="month" tick={{fill:'#8EAADF',fontSize:10}}/><YAxis tick={{fill:'#8EAADF',fontSize:10}} tickFormatter={v=>`$${v}`}/>
                <RTooltip contentStyle={{backgroundColor:'#0F2044',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'8px',color:'#fff',fontSize:11}}/><Legend/>
                {fareData?.fare_matrix?.map(a=><Line key={a.airline_iata} type="monotone" dataKey={a.airline_iata} name={a.airline_iata} stroke={AC[a.airline_iata]||'#666'} strokeWidth={a.is_our_airline?3:1.5} dot={{fill:AC[a.airline_iata]||'#666',r:2}} connectNulls/>)}
              </LineChart></ResponsiveContainer></div>
            </div>
          </div>
        </TabsContent>

        {/* GAPS */}
        <TabsContent value="gaps">
          <div className="bg-card-bg border border-[rgba(255,255,255,0.08)] rounded-xl overflow-x-auto">
            <div className="flex items-center justify-between p-4 border-b border-[rgba(255,255,255,0.08)]"><h3 className="font-syne font-bold text-white">Gap Analysis vs CI</h3>
              <Button variant="outline" size="sm" data-testid="export-csv-btn" onClick={()=>{if(!gapData?.gaps)return;let c='Airline,Class,'+gapData.months.map(m=>m.label).join(',')+'\n';gapData.gaps.forEach(r=>{c+=`${r.airline_iata},${r.cabin_class},`+r.gaps.map(g=>g.gap_nzd!=null?g.gap_nzd:'').join(',')+'\n'});const b=new Blob([c],{type:'text/csv'});const u=URL.createObjectURL(b);const a=document.createElement('a');a.href=u;a.download='gaps.csv';a.click()}} className="border-[rgba(255,255,255,0.15)] text-text-secondary hover:text-white h-7 text-xs">CSV</Button></div>
            <table className="w-full text-sm"><thead><tr className="border-b border-[rgba(255,255,255,0.08)]"><th className="sticky-col bg-card-bg text-left px-4 py-2 text-text-secondary text-xs min-w-[160px]">Airline/Class</th>
              {gapData?.months?.map(m=><th key={m.label} className="text-center px-2 py-2 text-text-secondary text-xs min-w-[90px]">{m.label}</th>)}</tr></thead>
            <tbody>{gapData?.gaps?.map((row,ri)=><tr key={ri} className="border-b border-[rgba(255,255,255,0.06)] hover:bg-white/5">
              <td className="sticky-col bg-card-bg px-4 py-1.5"><span className="text-white text-xs font-medium">{row.airline_iata}</span><span className="text-text-secondary text-[10px] ml-1 capitalize">{row.cabin_class.replace('_',' ')}</span></td>
              {row.gaps.map((g,gi)=><td key={gi} className={`px-2 py-1.5 text-center font-mono text-[10px] ${g.gap_nzd==null?'':g.gap_nzd>0?'bg-danger/10 text-danger':'bg-success/10 text-success'}`}>
                {g.gap_nzd!=null?<div><div>{g.gap_nzd>0?'+':''}${g.gap_nzd.toLocaleString()}</div><div className="opacity-70">{g.gap_percent>0?'+':''}{g.gap_percent}%</div></div>:<span className="text-text-secondary">---</span>}
              </td>)}</tr>)}</tbody></table>
          </div>
        </TabsContent>

        {/* CAPACITY (E4) */}
        <TabsContent value="capacity">
          {!capacityData ? <div className="skeleton h-48 rounded-xl"/> : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 bg-card-bg border border-[rgba(255,255,255,0.08)] rounded-xl p-4">
                  <h3 className="font-syne font-bold text-white mb-3 text-sm">Flight Capacity by Carrier</h3>
                  <table className="w-full text-xs" data-testid="capacity-table"><thead><tr className="border-b border-[rgba(255,255,255,0.08)]">
                    <th className="text-left px-2 py-1.5 text-text-secondary">Carrier</th><th className="text-left px-2 py-1.5 text-text-secondary">Weekly</th>
                    <th className="text-left px-2 py-1.5 text-text-secondary">Aircraft</th><th className="text-left px-2 py-1.5 text-text-secondary">Routing</th>
                  </tr></thead><tbody>
                    {[...capacityData.direct_carriers,...capacityData.sixth_freedom_carriers].map((c,i)=>(
                      <motion.tr key={i} initial={{opacity:0}} animate={{opacity:1}} transition={{delay:i*0.04}} className={`border-b border-[rgba(255,255,255,0.06)] ${c.airline_iata==='CI'?'ci-row':''}`}>
                        <td className="px-2 py-1.5"><span className="font-mono text-white font-medium">{c.airline_iata}</span><span className="text-text-secondary ml-1">{c.airline_name}</span></td>
                        <td className="px-2 py-1.5 font-mono text-white">{c.weekly_flights}</td>
                        <td className="px-2 py-1.5 text-text-secondary font-mono">{c.aircraft_type}</td>
                        <td className="px-2 py-1.5"><Badge variant="outline" className={`text-[9px] ${c.is_sixth_freedom?'border-amber-500/40 text-amber-400':'border-emerald-500/40 text-emerald-400'}`}>{c.routing}</Badge></td>
                      </motion.tr>
                    ))}
                  </tbody></table>
                </div>
                <div className="bg-card-bg border border-[rgba(255,255,255,0.08)] rounded-xl p-4">
                  <h3 className="font-syne font-bold text-white mb-3 text-sm">Market Share</h3>
                  <div className="h-48"><ResponsiveContainer width="100%" height="100%"><PieChart>
                    <Pie data={[...capacityData.direct_carriers,...capacityData.sixth_freedom_carriers].map(c=>({name:c.airline_iata,value:c.market_share_percent||0}))} innerRadius={50} outerRadius={80} dataKey="value" label={({name,value})=>`${name} ${value}%`}>
                      {[...capacityData.direct_carriers,...capacityData.sixth_freedom_carriers].map((c,i)=><PieCell key={i} fill={AC[c.airline_iata]||'#666'}/>)}
                    </Pie><RTooltip contentStyle={{backgroundColor:'#0F2044',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'8px',color:'#fff',fontSize:11}}/>
                  </PieChart></ResponsiveContainer></div>
                  <div className="text-center mt-2"><span className="font-mono text-ci-blue font-bold text-lg">CI: {capacityData.ci_market_share_percent}%</span><p className="text-text-secondary text-xs">{capacityData.ci_weekly_flights} flights/week of {capacityData.total_weekly_flights} total</p></div>
                </div>
              </div>
              <div className="bg-card-bg border-l-4 border-l-ci-orange border border-[rgba(255,255,255,0.08)] rounded-xl p-4">
                <p className="text-white text-sm">CI operates <span className="font-mono font-bold text-ci-blue">{capacityData.ci_weekly_flights}</span> flights/week on this route, representing <span className="font-mono font-bold text-ci-orange">{capacityData.ci_market_share_percent}%</span> of total weekly capacity ({capacityData.total_weekly_flights} flights).</p>
              </div>
            </div>
          )}
        </TabsContent>

        {/* CI PROMO TRACKER */}
        <TabsContent value="ci-promo">
          {promoLoading ? <div className="skeleton h-48 rounded-xl" data-testid="ci-promo-skeleton"/>
          : !promoData ? (
            <div className="bg-card-bg border border-[rgba(255,255,255,0.08)] rounded-xl p-12 text-center">
              <p className="text-text-secondary">Loading CI Promo data…</p>
            </div>
          ) : promoData.error ? (
            <div className="bg-card-bg border border-[rgba(255,255,255,0.08)] rounded-xl p-8 text-center text-red-400">Failed to load promo tracker</div>
          ) : (
            <div className="space-y-4 max-w-6xl mx-auto" data-testid="ci-promo-panel">
              {/* Featured today quick-link chips */}
              {featuredToday && featuredToday.length > 0 && (
                <div
                  data-testid="ci-promo-featured-today"
                  className="bg-card-bg border border-[rgba(255,255,255,0.08)] border-l-4 border-l-emerald-500 rounded-xl p-4"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <p className="text-xs uppercase tracking-wider text-emerald-400 font-semibold">
                      Featured today on CI NZ homepage · {featuredToday.length} {featuredToday.length === 1 ? "route" : "routes"}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {featuredToday.map(f => {
                      const active = (promoRouteId || routeId) === f.route_id;
                      return (
                        <button
                          key={f.route_id}
                          onClick={() => { setPromoRouteId(f.route_id); fetchPromo(f.route_id); }}
                          data-testid={`ci-promo-chip-${f.destination_code}`}
                          className={`text-xs font-mono px-3 py-1.5 rounded-full border transition-all ${
                            active
                              ? "bg-emerald-500 text-white border-emerald-500"
                              : "bg-emerald-500/10 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/20"
                          }`}
                        >
                          {f.origin_code}→{f.destination_code}
                          {f.fare_nzd ? <span className="ml-1.5 opacity-80">NZ${f.fare_nzd.toLocaleString()}</span> : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Compare-all-routes dropdown */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-card-bg border border-[rgba(255,255,255,0.08)] rounded-xl p-4">
                <label className="text-xs uppercase tracking-wider text-text-secondary shrink-0">Compare route:</label>
                <select
                  data-testid="ci-promo-route-selector"
                  value={promoRouteId || routeId}
                  onChange={e => { setPromoRouteId(e.target.value); fetchPromo(e.target.value); }}
                  className="bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.15)] rounded-lg text-white text-sm px-3 py-2 flex-1 max-w-md focus:outline-none focus:border-ci-blue"
                >
                  {(allRoutes.length ? allRoutes : [{id:routeId,origin_code:fareData?.route?.origin_code||"AKL",destination_code:fareData?.route?.destination_code||"—"}]).map(r => (
                    <option key={r.id} value={r.id} className="bg-[#0A1428]">
                      {r.origin_code}→{r.destination_code}{r.id === routeId ? "  (this route)" : ""}
                    </option>
                  ))}
                </select>
                {promoRouteId && promoRouteId !== routeId && (
                  <button onClick={()=>{setPromoRouteId(null); fetchPromo(routeId);}}
                    className="text-xs text-ci-blue hover:text-white px-3 py-1.5 rounded-lg border border-[rgba(255,255,255,0.15)]"
                    data-testid="ci-promo-reset-btn">
                    Reset to this route
                  </button>
                )}
              </div>

              {/* Empty state: route has never been featured in the last 30 days */}
              {promoData.days_featured_30d === 0 ? (
                <div
                  data-testid="ci-promo-empty-state"
                  className="bg-card-bg border border-[rgba(255,255,255,0.08)] rounded-xl p-12 text-center"
                >
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gray-500/10 mb-4">
                    <Warning size={26} weight="duotone" className="text-text-secondary" />
                  </div>
                  <h3 className="font-syne font-bold text-white text-lg mb-2">
                    Not featured on CI NZ homepage
                  </h3>
                  <p className="text-text-secondary text-sm max-w-md mx-auto">
                    CI has not promoted this route on their New Zealand homepage in the last 30 days.
                    {featuredToday && featuredToday.length > 0
                      ? " Use the chips above to jump to a currently-featured route."
                      : " No routes are currently featured."}
                  </p>
                  <p className="text-[10px] text-text-secondary mt-4 font-mono">
                    Source: {promoData.snapshot_source || "CI Direct Scraper"}
                  </p>
                </div>
              ) : (
              <>
              {/* Top summary row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-card-bg border border-[rgba(255,255,255,0.08)] rounded-xl p-4">
                  <p className="text-[10px] uppercase tracking-wider text-text-secondary">Featured today</p>
                  <div className="mt-1 flex items-center gap-2">
                    {promoData.currently_featured
                      ? <><CheckCircle size={18} weight="fill" className="text-emerald-400"/><span className="text-white font-bold text-lg" data-testid="promo-currently-featured">Yes</span></>
                      : <><span className="inline-block w-3 h-3 rounded-full bg-gray-500"/><span className="text-gray-300 font-bold text-lg" data-testid="promo-currently-featured">No</span></>}
                  </div>
                </div>
                <div className="bg-card-bg border border-[rgba(255,255,255,0.08)] rounded-xl p-4">
                  <p className="text-[10px] uppercase tracking-wider text-text-secondary">Current fare (Economy)</p>
                  <p className="mt-1 font-mono text-ci-blue font-bold text-xl" data-testid="promo-current-fare">
                    {promoData.current_fare_nzd ? `NZD $${promoData.current_fare_nzd.toLocaleString()}` : "—"}
                  </p>
                </div>
                <div className="bg-card-bg border border-[rgba(255,255,255,0.08)] rounded-xl p-4">
                  <p className="text-[10px] uppercase tracking-wider text-text-secondary">First detected</p>
                  <p className="mt-1 text-white font-mono text-sm" data-testid="promo-first-detected">
                    {promoData.first_detected || "Not yet featured"}
                  </p>
                </div>
                <div className="bg-card-bg border border-[rgba(255,255,255,0.08)] rounded-xl p-4">
                  <p className="text-[10px] uppercase tracking-wider text-text-secondary">Days featured (last 30)</p>
                  <p className="mt-1 font-mono text-white font-bold text-xl" data-testid="promo-days-featured">
                    {promoData.days_featured_30d} / 30
                  </p>
                </div>
              </div>

              {/* 30-day strip */}
              <div className="bg-card-bg border border-[rgba(255,255,255,0.08)] rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-syne font-bold text-white text-sm">30-day featured history</h3>
                  <p className="text-[10px] text-text-secondary">Source: {promoData.snapshot_source}</p>
                </div>
                <div className="flex gap-1 overflow-x-auto pb-1" data-testid="promo-history-strip">
                  {promoData.history.map((d, i) => (
                    <TooltipProvider key={i} delayDuration={100}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className={`shrink-0 w-6 h-10 rounded-sm transition-colors ${
                              !d.tracked ? "bg-gray-800/40 border border-gray-700"
                              : d.featured ? "bg-emerald-500 hover:bg-emerald-400"
                              : "bg-gray-600/50 hover:bg-gray-500/60"
                            }`}
                            data-testid={`promo-day-${d.date}`}
                          />
                        </TooltipTrigger>
                        <TooltipContent className="bg-black/90 border-gray-700 text-white text-xs">
                          <p className="font-mono">{d.date}</p>
                          <p>{d.featured ? `Featured · NZD $${d.fare_nzd?.toLocaleString()}` : "Not featured"}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-4 text-[10px] text-text-secondary">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 bg-emerald-500 rounded-sm inline-block"/> Featured</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 bg-gray-600/60 rounded-sm inline-block"/> Not featured</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 bg-gray-800/40 border border-gray-700 rounded-sm inline-block"/> Not tracked</span>
                </div>
              </div>

              {/* Fare-variation line (featured days only) */}
              {promoData.history.some(d => d.featured && d.fare_nzd) && (
                <div className="bg-card-bg border border-[rgba(255,255,255,0.08)] rounded-xl p-5">
                  <h3 className="font-syne font-bold text-white text-sm mb-3">Fare variation on featured days</h3>
                  <div className="h-48">
                    <ResponsiveContainer width="99%" height="100%">
                      <LineChart data={promoData.history.filter(d => d.featured && d.fare_nzd)}>
                        <XAxis dataKey="date" tick={{fontSize:10, fill:"#8EAADF"}} tickFormatter={d=>d?.slice(5)}/>
                        <YAxis tick={{fontSize:10, fill:"#8EAADF"}} domain={["auto","auto"]} tickFormatter={v=>`$${v}`}/>
                        <RTooltip contentStyle={{background:"rgba(10,20,40,0.95)", border:"1px solid rgba(255,255,255,0.15)", color:"#fff", fontSize:"12px"}} formatter={v=>[`NZD $${v?.toLocaleString()}`, "Fare"]}/>
                        <Line type="monotone" dataKey="fare_nzd" stroke="#1B52C4" strokeWidth={2} dot={{fill:"#1B52C4", r:3}}/>
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
              </>
              )}
            </div>
          )}
        </TabsContent>

        {/* WEEKLY OW TRACKER */}
        {isBNE && (
          <TabsContent value="weekly">
            {weeklyLoading ? <div className="skeleton h-48 rounded-xl"/>
            : !weeklyData || weeklyData.weeks?.every(w=>!w.airlines.CI.economy.fare_nzd&&!w.airlines.NZ.economy.fare_nzd) ? (
              <div className="bg-card-bg border border-[rgba(255,255,255,0.08)] rounded-xl p-12 text-center">
                <CalendarBlank size={48} className="text-text-secondary mx-auto mb-4" weight="duotone"/>
                <h3 className="font-syne font-bold text-white text-lg mb-2">No weekly data yet</h3>
                <Button onClick={handleWeeklyRefresh} disabled={weeklyRefreshing} data-testid="fetch-weekly-btn" className="bg-ci-orange hover:bg-ci-orange/90 text-white h-10 px-6">
                  <ArrowsClockwise size={16} className={weeklyRefreshing?"spin mr-2":"mr-2"}/>Fetch Weekly Data</Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-between"><h3 className="font-syne font-bold text-white">Weekly OW Tracker</h3>
                  <Button onClick={handleWeeklyRefresh} disabled={weeklyRefreshing} data-testid="refresh-weekly-btn" className="bg-ci-orange hover:bg-ci-orange/90 text-white h-8 px-3 text-xs"><ArrowsClockwise size={14} className={weeklyRefreshing?"spin mr-1":"mr-1"}/>Refresh</Button></div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {["economy","premium_economy","business"].map(cc=>{const s=weeklyData.summary?.[cc]||{};return(
                    <div key={cc} className="bg-card-bg border border-[rgba(255,255,255,0.08)] rounded-xl p-4">
                      <h4 className="font-syne font-bold text-white text-xs mb-2">{cc==="economy"?"Economy (Y)":cc==="premium_economy"?"Premium (PY)":"Business (C)"}</h4>
                      {s.avg_gap_nzd<0?<p className="text-success text-xs">CI cheaper on <span className="font-mono font-bold">{s.ci_cheaper_count}</span>/{s.total_weeks}</p>
                        :<p className="text-danger text-xs">NZ cheaper on <span className="font-mono font-bold">{s.nz_cheaper_count}</span>/{s.total_weeks}</p>}
                      <p className="text-text-secondary text-[10px] mt-1">Avg: <span className={`font-mono font-bold ${s.avg_gap_nzd<0?'text-success':'text-danger'}`}>{s.avg_gap_nzd<0?'-':'+'}NZ${Math.abs(s.avg_gap_nzd||0)}</span></p>
                    </div>)})}
                </div>
                <div className="bg-card-bg border border-[rgba(255,255,255,0.08)] rounded-xl overflow-x-auto">
                  <table className="w-full text-[10px]" data-testid="weekly-table"><thead>
                    <tr className="border-b border-[rgba(255,255,255,0.08)]"><th colSpan={2} className="sticky-col bg-card-bg px-2 py-1"></th>
                      {["Economy","Premium","Business"].map(l=><th key={l} colSpan={3} className="text-center px-1 py-1 text-text-secondary border-l border-[rgba(255,255,255,0.08)]">{l}</th>)}</tr>
                    <tr className="border-b border-[rgba(255,255,255,0.08)] bg-card-bg"><th className="sticky-col bg-card-bg px-2 py-1 text-text-secondary text-left">Day</th><th className="px-2 py-1 text-text-secondary text-left">Date</th>
                      {[0,1,2].map(i=><React.Fragment key={i}><th className="px-1 py-1 text-text-secondary text-center border-l border-[rgba(255,255,255,0.08)]">CI</th><th className="px-1 py-1 text-text-secondary text-center">NZ</th><th className="px-1 py-1 text-text-secondary text-center">Gap</th></React.Fragment>)}</tr>
                  </thead><tbody>
                    {weeklyData.weeks?.map((w,wi)=>{const d=new Date(w.departure_date+'T00:00:00');return(
                      <motion.tr key={wi} initial={{opacity:0}} animate={{opacity:1}} transition={{delay:wi*0.04}} className="border-b border-[rgba(255,255,255,0.06)] hover:bg-white/5">
                        <td className="sticky-col bg-card-bg px-2 py-1.5 text-text-secondary font-mono">TUE</td>
                        <td className="px-2 py-1.5 text-white font-mono whitespace-nowrap">{d.toLocaleDateString('en-NZ',{day:'numeric',month:'short',year:'2-digit'})}</td>
                        {["economy","premium_economy","business"].map(cc=>{const ci=w.airlines?.CI?.[cc]||{},nz=w.airlines?.NZ?.[cc]||{},gap=w.gaps?.[cc]||{};
                          const gBg=gap.ci_has_no_flight?'bg-white/5':gap.gap_nzd==null?'':Math.abs(gap.gap_nzd||0)<=20?'bg-warning/10':gap.ci_cheaper?'bg-success/10':'bg-danger/10';
                          const gC=gap.ci_has_no_flight?'text-gray-500':gap.gap_nzd==null?'text-text-secondary':Math.abs(gap.gap_nzd||0)<=20?'text-warning':gap.ci_cheaper?'text-success':'text-danger';
                          return(<React.Fragment key={cc}>
                            <td className={`px-1 py-1.5 text-center font-mono border-l border-[rgba(255,255,255,0.08)] ${ci.has_no_flight?'bg-white/[0.02]':''}`}>{ci.has_no_flight?<span className="text-gray-500 italic">&mdash;</span>:ci.fare_nzd?<span className="text-white">${ci.fare_nzd.toLocaleString()}</span>:<span className="text-gray-600">&middot;&middot;&middot;</span>}</td>
                            <td className="px-1 py-1.5 text-center font-mono">{nz.has_no_flight?<span className="text-gray-500 italic">&mdash;</span>:nz.fare_nzd?<span className="text-white">${nz.fare_nzd.toLocaleString()}</span>:<span className="text-gray-600">&middot;&middot;&middot;</span>}</td>
                            <td className={`px-1 py-1.5 text-center font-mono ${gBg} ${gC}`}>{gap.ci_has_no_flight?'n/a':gap.gap_nzd!=null?<>{gap.gap_nzd<0?'-':'+'}${Math.abs(gap.gap_nzd)}</>:'---'}</td>
                          </React.Fragment>);})}
                      </motion.tr>);})}
                  </tbody></table>
                </div>
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
